import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbSearch, TbUsers, TbUser, TbExternalLink } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { apiRequest, ApiError } from "src/services/apiClient";
import { templateCodesText, templateDayCount, templateTotal, type InvoiceTemplate, type TemplateLine } from "src/helpers/invoiceTemplates";
import "./templateDialogs.scss";

const formatEuro = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Step 3, story 13: pick one of the viewer's / the practice's templates to add to a treatment day. */
export function InsertTemplateDialog({
    templates,
    loading,
    onPick,
    onClose,
}: {
    templates: InvoiceTemplate[];
    loading: boolean;
    onPick: (template: InvoiceTemplate) => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const c = (key: string, opts?: Record<string, unknown>) => t(`invoiceTemplates.${key}`, opts);
    const [query, setQuery] = useState("");
    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? templates.filter((tpl) => tpl.name.toLowerCase().includes(q) || tpl.lines.some((l) => l.code.toLowerCase() === q)) : templates;
    }, [templates, query]);

    return (
        <Popup
            title={c("insertTitle")}
            className="ic-template-popup"
            message={
                <div className="ic-templates">
                    {templates.length > 5 && (
                        <label className="ic-templates__search">
                            <TbSearch aria-hidden />
                            <input
                                type="search"
                                value={query}
                                placeholder={c("searchPlaceholder")}
                                aria-label={c("searchPlaceholder")}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </label>
                    )}
                    {loading && templates.length === 0 ? (
                        <p className="ic-templates__empty">{c("loading")}</p>
                    ) : templates.length === 0 ? (
                        <div className="ic-templates__empty">
                            <p>{c("emptyWizard")}</p>
                            <a href="/settings?section=invoice-templates" target="_blank" rel="noreferrer">
                                {c("manage")} <TbExternalLink aria-hidden />
                            </a>
                        </div>
                    ) : shown.length === 0 ? (
                        <p className="ic-templates__empty">{c("noMatch", { query: query.trim() })}</p>
                    ) : (
                        <ul className="ic-templates__list" aria-label={c("insertTitle")}>
                            {shown.map((tpl) => (
                                <li key={tpl.id}>
                                    <button type="button" className="ic-templates__item" onClick={() => onPick(tpl)}>
                                        <span className="ic-templates__name">
                                            {tpl.name}
                                            <span className="ic-templates__scope">
                                                {tpl.shared ? <TbUsers aria-hidden /> : <TbUser aria-hidden />}
                                                {tpl.shared ? c("shared") : c("personal")}
                                            </span>
                                        </span>
                                        <span className="ic-templates__meta">
                                            {templateDayCount(tpl) > 1 && <>{c("dayCount", { count: templateDayCount(tpl) })} · </>}
                                            {templateCodesText(tpl, (n) => c("dayN", { n }))} · {formatEuro(templateTotal(tpl))}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            }
            actions={[{ label: t("popup.cancel"), variant: "default", onClick: onClose }]}
            onClose={onClose}
        />
    );
}

/** Save lines as a new template — personal, or for the whole practice. Step 3 (one day, or with `allDaysLines`
 *  the choice of all treatment days) and the invoice page (an invoice's services, `note` says what was left out). */
export function SaveTemplateDialog({
    lines,
    allDaysLines,
    note,
    onSaved,
    onClose,
}: {
    lines: TemplateLine[];
    /** Offered as "Alle Behandlungstage" next to "Nur dieser Tag". */
    allDaysLines?: TemplateLine[];
    note?: string;
    onSaved: (template: InvoiceTemplate) => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const c = (key: string, opts?: Record<string, unknown>) => t(`invoiceTemplates.${key}`, opts);
    const nameId = useId();
    const [name, setName] = useState("");
    const [shared, setShared] = useState(false);
    const [scope, setScope] = useState<"day" | "all">("day");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const chosen = scope === "all" && allDaysLines ? allDaysLines : lines;
    const days = templateDayCount({ lines: chosen });

    const save = async () => {
        if (!name.trim() || saving) return;
        setSaving(true);
        setError(null);
        try {
            const created = await apiRequest<InvoiceTemplate>(
                "/invoice-templates",
                { method: "POST", body: JSON.stringify({ name: name.trim(), shared, lines: chosen }) },
                { "Content-Type": "application/json" },
            );
            onSaved(created);
        } catch (e) {
            setError(templateError(e, t));
            setSaving(false);
        }
    };

    return (
        <Popup
            title={c("saveTitle")}
            className="ic-template-popup"
            message={
                <form className="ic-template-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
                    <label className="ic-template-form__field" htmlFor={nameId}>
                        <span>{c("name")}</span>
                        <input id={nameId} type="text" value={name} maxLength={80} placeholder={c("namePlaceholder")} autoFocus onChange={(e) => setName(e.target.value)} />
                    </label>
                    {allDaysLines && (
                        <fieldset className="ic-template-form__scope">
                            <legend>{c("saveScope")}</legend>
                            <label>
                                <input type="radio" name={`${nameId}-scope`} checked={scope === "day"} onChange={() => setScope("day")} />
                                <span>{c("scopeDay")}</span>
                            </label>
                            <label>
                                <input type="radio" name={`${nameId}-scope`} checked={scope === "all"} onChange={() => setScope("all")} />
                                <span>{c("scopeAll", { count: templateDayCount({ lines: allDaysLines }) })}</span>
                            </label>
                        </fieldset>
                    )}
                    <label className="ic-template-form__check">
                        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
                        <span>{c("shareWithPractice")}</span>
                    </label>
                    <p className="ic-template-form__lines">
                        {days > 1
                            ? c("linesToSaveDays", { count: chosen.length, days, codes: templateCodesText({ lines: chosen }, (n) => c("dayN", { n })) })
                            : c("linesToSave", { count: chosen.length, codes: chosen.map((l) => l.code).join(", ") })}
                    </p>
                    {note && <p className="ic-template-form__note">{note}</p>}
                    {error && <p className="ic-template-form__error" role="alert">{error}</p>}
                </form>
            }
            actions={[
                { label: t("popup.cancel"), variant: "default", onClick: onClose },
                { label: saving ? c("saving") : c("save"), variant: "primary", disabled: saving || !name.trim(), onClick: () => void save() },
            ]}
            onClose={onClose}
        />
    );
}

/** The server's reason as text (shared with Settings → Rechnungsvorlagen). */
export function templateError(e: unknown, t: (key: string, opts?: Record<string, unknown>) => string) {
    const code = e instanceof ApiError ? e.body?.error : undefined;
    const known = ["NAME_TAKEN", "TOO_MANY_TEMPLATES", "INVALID_NAME", "INVALID_LINES", "NOT_YOURS"];
    return t(`invoiceTemplates.errors.${known.includes(code) ? code : "generic"}`, { limit: e instanceof ApiError ? e.body?.limit : undefined });
}
